import clsx from "clsx"
import type { KeyboardEvent, MouseEvent } from "react"

import audioPlaceholder from "../../images/audioPlaceholder.svg"
import filePlaceholder from "../../images/filePlaceholder.svg"
import videoPlaceholder from "../../images/videoPlaceholder.svg"
import { Badge } from "../Badge"
import { Box } from "../Box"
import { Checkbox } from "../Checkbox"
import { CheckerBoard } from "../CheckerBoard"
import { ContentEditable } from "../ContentEditable"
import { Image } from "../Image"
import { MediaProgress, type Status } from "../MediaProgress/MediaProgress"
import {
  OverflowContainer,
  OverflowEndAdornment,
  OverflowItem
} from "../OverflowContainer/OverflowContainer"
import { Text } from "../Text"
import styles from "./MediaCard.module.css"

const previewHeight = 186

const placeholders: Record<string, string> = {
  pdf: filePlaceholder,
  csv: filePlaceholder,
  txt: filePlaceholder,
  application: filePlaceholder,
  mp4: videoPlaceholder,
  mov: videoPlaceholder,
  avi: videoPlaceholder,
  mkv: videoPlaceholder,
  video: videoPlaceholder,
  mp3: audioPlaceholder,
  wav: audioPlaceholder,
  audio: audioPlaceholder
}

interface Tag {
  id: string
  name: string
}

interface MediaCardProps {
  "aria-label"?: string
  url: string
  filename: string
  filenameHighlight?: string
  alt?: string
  /**
   * The file extension of the media
   * @example "png"
   */
  extension?: string
  kind?: string
  tags?: Tag[]
  onAddTag?: () => void
  width?: number
  height?: number
  selected?: boolean
  loading?: boolean
  /**
   * The upload status of the media
   * @default idle
   */
  status?: Status
  /**
   * The size of the media in bytes
   */
  size?: number
  /**
   * Function called when the card is clicked
   * @description This only affects the card, not the checkbox
   */
  onCardClick?: () => void
  /**
   * Function called when the checkbox is clicked
   * @description This only affects the checkbox, not the card
   * @param checked - The new checked state of the checkbox
   */
  onCheckedChange?: (checked: boolean) => void
}

export function MediaCard(props: MediaCardProps) {
  const {
    ["aria-label"]: ariaLabel,
    url,
    filename,
    filenameHighlight,
    selected = false,
    loading = false,
    status = "idle",
    extension,
    tags,
    onAddTag,
    width,
    height,
    size,
    kind,
    alt,
    onCheckedChange,
    onCardClick
  } = props

  function getMetadata() {
    if (extension === "pdf" && size) {
      return `${size}kb`
    }

    if (width && height) {
      return `${width}x${height}`
    }

    return ""
  }

  function onClick() {
    if (loading) return
    onCardClick?.()
  }

  function onKeyDown(evt: KeyboardEvent<HTMLDivElement>) {
    if (evt.code === "Enter" || evt.code === "Space") {
      evt.preventDefault() // Prevents scroll using Space
      onClick()
    }
  }

  const cardTitle = filenameHighlight ?? filename

  return (
    <Box aria-label={ariaLabel}>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={onClick}
        className={clsx(styles.root, selected && styles.selected)}
      >
        <Box flexDirection="column" width="100%">
          <Preview
            extension={extension}
            url={url}
            filename={filename}
            loading={loading}
            kind={kind}
            alt={alt}
          />
        </Box>

        <Box
          flexDirection="column"
          width="100%"
          alignItems="flex-start"
          opacity={loading ? 0.3 : 1}
        >
          <ContentEditable
            content={cardTitle}
            contentVersion={cardTitle}
            decorations={{ em: "highlight", p: "emphasized" }}
            readOnly
          />

          {/* Since some items have no dimensions a fixed height is required in order for the layout to not collapse */}
          <Text variant="small" color="grey11" sx={{ height: 16 }}>
            {getMetadata()}
          </Text>

          <MediaProgress status={status} />

          <Box width="100%" alignItems="center" gap={8}>
            <ExtensionBadge extension={extension} />
            <MediaCardTags tags={tags} onAddTag={onAddTag} />
          </Box>
        </Box>
      </div>

      <div className={styles.checkbox}>
        <Checkbox
          onCheckedChange={onCheckedChange}
          checked={selected}
          onClick={(event) => event.stopPropagation()}
          disabled={loading}
        />
      </div>
    </Box>
  )
}

interface PreviewProps {
  filename: string
  url: string
  extension?: string
  loading: boolean
  kind?: string
  alt?: string
}

function Preview(props: PreviewProps) {
  const { extension, url, filename, loading, kind, alt } = props
  const placeholder = kind?.startsWith("image")
    ? undefined
    : getPlaceholder(extension)

  const opacity = loading ? 0.3 : 1

  if (placeholder) {
    return (
      <Box
        width="100%"
        height={previewHeight}
        backgroundColor="purple2"
        justifyContent="center"
        alignItems="center"
        opacity={opacity}
      >
        <MediaCardPlaceholder extension={extension} filename={filename} />
      </Box>
    )
  }

  return (
    <CheckerBoard>
      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        height={previewHeight}
        width="100%"
        opacity={opacity}
      >
        <Image src={url} alt={alt || filename} sizing="contain" />
      </Box>
    </CheckerBoard>
  )
}

interface MediaCardPlaceholderProps {
  filename: string
  extension?: string
}

export function MediaCardPlaceholder(props: MediaCardPlaceholderProps) {
  const { extension, filename } = props
  const placeholder = getPlaceholder(extension)

  return <img src={placeholder} alt={filename} />
}

/**
 * @private - Get the placeholder for a given file extension
 *
 * Will pass through all the file extensions and return the placeholder for the first match
 * Since extensions can be formatted in different ways, we will use the `startsWith` method to check if the given extension starts with the key
 * During an upload the extension can be formatted as `video/mp4` and as `mp4` when returned from the server, so we need to check for both
 *
 * @param extension - The file extension
 * @returns - the asset path for the placeholder
 */
function getPlaceholder(extension?: string) {
  if (!extension) return filePlaceholder

  return Object.entries(placeholders).find(([key]) => {
    return extension.startsWith(key)
  })?.[1]
}

interface MediaCardTagsProps {
  tags?: Tag[]
  onAddTag?: () => void
}

function MediaCardTags(props: MediaCardTagsProps) {
  const { tags, onAddTag } = props

  function onClick(event?: MouseEvent<HTMLDivElement>) {
    event?.stopPropagation()
    onAddTag?.()
  }

  /**
   * We can only ever fit a max of around 15 tags on the first line
   * of the media card, so in order to prevent the Overflow component
   * from having to handle a possibly very large number of tags.
   * Limit the ones sent to overflow to 15
   */
  const maxNumberOfDisplaytags = 15

  const noTags = !tags || tags.length === 0

  if (!onAddTag && noTags) return null

  if (noTags)
    return <Badge title="Add a tag +" color="purple" onClick={onClick} />

  return (
    <OverflowContainer>
      {tags.slice(0, maxNumberOfDisplaytags).map(({ name, id }) => (
        <OverflowItem key={id}>
          <Badge title={name} color="purple" size="medium" />
        </OverflowItem>
      ))}

      <OverflowEndAdornment
        adornment={({ numberOfVisibleItems }) => (
          <Badge
            title={`+${tags.length - numberOfVisibleItems}`}
            color="purple"
            size="medium"
          />
        )}
      />
    </OverflowContainer>
  )
}

interface ExtensionBadgeProps {
  extension?: string
}
function ExtensionBadge(props: ExtensionBadgeProps) {
  const { extension } = props

  if (!extension) return null
  return <Badge size="medium" title={extension?.toUpperCase() ?? ""} selected />
}
